<template>
  <Teleport to="body">
    <Transition name="panel-fade">
      <div
        v-if="store.showPortfolio"
        class="portfolio-overlay"
        @click.self="store.closePortfolio"
      >
        <div class="portfolio-panel" role="dialog" aria-labelledby="portfolio-title">
          
          <!-- Header: Tabs + Account + Actions -->
          <div class="portfolio-header">
            <div class="portfolio-tabs-wrapper">
              <button
                class="portfolio-tab"
                :class="{ active: activeTab === 'portfolio' }"
                @click="activeTab = 'portfolio'"
              >
                Varlıklarım
              </button>
              <button
                class="portfolio-tab"
                :class="{ active: activeTab === 'orders' }"
                @click="activeTab = 'orders'"
              >
                Emirlerim
              </button>
              <button
                class="portfolio-tab"
                :class="{ active: activeTab === 'summary' }"
                @click="activeTab = 'summary'"
              >
                Hesap Özeti
              </button>
            </div>

            <div class="portfolio-header-right">
              <!-- Account Selector -->
              <div v-if="store.accounts && store.accounts.length > 0" class="header-control account-selector">
                <CreditCard :size="14" class="control-icon" />
                <select
                  v-model="selectedAccountLocal"
                  class="account-select"
                  :disabled="store.accountsLoading"
                  @change="onAccountChange"
                >
                  <option
                    v-for="acc in store.accounts"
                    :key="acc.id"
                    :value="acc.id"
                  >
                    {{ acc.name || acc.id }}
                  </option>
                </select>
                <ChevronDown :size="14" class="select-chevron" />
              </div>

              <!-- Disconnect Button -->
              <button class="header-control disconnect-btn" @click="handleDisconnect" title="Bağlantıyı Kes">
                <LogOut :size="16" />
                <span class="control-label">Çıkış</span>
              </button>

              <!-- Close Button -->
              <button class="close-btn" @click="store.closePortfolio()" title="Kapat">
                <X :size="20" />
              </button>
            </div>
          </div>

          <!-- Content -->
          <div class="portfolio-content">
            <!-- Loading State -->
            <div v-if="store.portfolioLoading && !store.portfolioData" class="portfolio-loading">
              <Loader2 :size="28" class="spin" />
              <span>Portföy yükleniyor...</span>
            </div>

            <!-- Varlıklarım Tab -->
            <template v-else-if="activeTab === 'portfolio'">
              <div v-if="portfolioPositions.length > 0" class="tab-content">
                <!-- KPI Cards -->
                <div class="kpi-grid">
                  <div class="kpi-card">
                    <span class="kpi-label">Toplam Portföy Değeri</span>
                    <div class="kpi-value-row">
                      <span class="kpi-value">{{ formatCurrency(totalValue) }}</span>
                      <span class="kpi-badge neutral">BIST Portföyü</span>
                    </div>
                  </div>
                  <div class="kpi-card">
                    <span class="kpi-label">Toplam Kar / Zarar</span>
                    <div class="kpi-value-row">
                      <span class="kpi-value" :class="totalPnl >= 0 ? 'profit' : 'loss'">
                        {{ totalPnl >= 0 ? '+' : '' }}{{ formatCurrency(totalPnl) }}
                      </span>
                      <span class="kpi-badge" :class="totalPnl >= 0 ? 'profit' : 'loss'">
                        <TrendingUp v-if="totalPnl >= 0" :size="12" />
                        <TrendingDown v-else :size="12" />
                        <span>{{ totalPnlPercent }}%</span>
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Distribution Bar -->
                <div v-if="distributionData.length > 0" class="distribution-section">
                  <div class="distribution-header">
                    <span>Ağırlıklı Varlık Dağılımı</span>
                    <span class="distribution-legend">
                      {{ distributionData.slice(0, 3).map(d => `${d.symbol} (%${d.percent.toFixed(1)})`).join(' • ') }}
                      <template v-if="distributionData.length > 3"> • Diğer</template>
                    </span>
                  </div>
                  <div class="distribution-bar">
                    <div
                      v-for="(item, idx) in distributionData.slice(0, 5)"
                      :key="item.symbol"
                      class="distribution-segment"
                      :class="getDistributionColor(idx)"
                      :style="{ width: item.percent + '%' }"
                    ></div>
                  </div>
                </div>

                <!-- Portfolio Table -->
                <div class="table-wrapper">
                  <table class="portfolio-table">
                    <thead>
                      <tr>
                        <th class="text-left">Sembol</th>
                        <th class="text-right">Adet</th>
                        <th class="text-right">Ort. Maliyet</th>
                        <th class="text-right">Toplam Değer</th>
                        <th class="text-right">Net K/Z</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="pos in portfolioPositions" :key="pos.symbol" class="table-row">
                        <td class="symbol-cell">
                          <span class="pnl-dot" :class="pos.pnl > 0 ? 'profit' : pos.pnl < 0 ? 'loss' : 'neutral'"></span>
                          <span class="symbol-name">{{ pos.symbol }}</span>
                        </td>
                        <td class="text-right text-secondary">{{ formatNum(pos.quantity) }}</td>
                        <td class="text-right text-muted">{{ formatCurrency(pos.cost) }}</td>
                        <td class="text-right text-primary font-semibold">{{ formatCurrency(pos.value) }}</td>
                        <td class="text-right" :class="pos.pnl >= 0 ? 'text-profit' : 'text-loss'">
                          {{ pos.pnl >= 0 ? '+' : '' }}{{ formatCurrency(pos.pnl) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-else class="empty-state">
                <Briefcase :size="40" class="empty-icon" />
                <span>Portföyde varlık bulunamadı</span>
              </div>
            </template>

            <!-- Emirlerim Tab -->
            <template v-else-if="activeTab === 'orders'">
              <div v-if="ordersList.length > 0" class="tab-content">
                <div class="table-wrapper">
                  <table class="portfolio-table">
                    <thead>
                      <tr>
                        <th class="text-left">Sembol</th>
                        <th class="text-center">İşlem</th>
                        <th class="text-right">Fiyat</th>
                        <th class="text-right">Adet</th>
                        <th class="text-right">Gerçekleşen</th>
                        <th class="text-center">Durum</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(order, idx) in ordersList" :key="idx" class="table-row">
                        <td class="symbol-cell">
                          <span class="symbol-name">{{ order.symbol }}</span>
                        </td>
                        <td class="text-center">
                          <span class="order-side-badge" :class="order.side === 'BUY' ? 'buy' : 'sell'">
                            {{ order.side === 'BUY' ? 'AL' : 'SAT' }}
                          </span>
                        </td>
                        <td class="text-right text-secondary">{{ formatCurrency(order.price) }}</td>
                        <td class="text-right text-secondary">{{ formatNum(order.quantity) }}</td>
                        <td class="text-right text-secondary">{{ formatNum(order.filled) }}</td>
                        <td class="text-center">
                          <span class="order-status-badge" :class="getOrderStatusClass(order.status)">
                            {{ orderStatusLabel(order.status) }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-else class="empty-state">
                <FileText :size="40" class="empty-icon" />
                <span>Günlük emir bulunamadı</span>
              </div>
            </template>

            <!-- Hesap Özeti Tab -->
            <template v-else-if="activeTab === 'summary'">
              <div v-if="accountSummary" class="tab-content">
                <div class="summary-grid">
                  <div v-for="(value, key) in accountSummary" :key="key" class="summary-item">
                    <span class="summary-label">{{ formatKey(String(key)) }}</span>
                    <span class="summary-value">{{ formatSummaryValue(value) }}</span>
                  </div>
                </div>
              </div>
              <div v-else class="empty-state">
                <Calculator :size="40" class="empty-icon" />
                <span>Hesap özeti bilgisi yok</span>
              </div>
            </template>
          </div>

          <!-- Footer: Refresh -->
          <div class="portfolio-footer">
            <span class="footer-hint">Son güncelleme: {{ lastUpdateTime }}</span>
            <button class="refresh-btn" @click="store.fetchPortfolio()" :disabled="store.portfolioLoading">
              <RefreshCw :size="14" :class="{ spin: store.portfolioLoading }" />
              <span>Yenile</span>
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { 
  LogOut, 
  RefreshCw, 
  Loader2, 
  CreditCard, 
  TrendingUp, 
  TrendingDown,
  Briefcase,
  FileText,
  Calculator,
  X,
  ChevronDown
} from 'lucide-vue-next'
import { useTraderMcpStore } from '@/stores/traderMcp'

const store = useTraderMcpStore()
const activeTab = ref<'portfolio' | 'orders' | 'summary'>('portfolio')

// Local account selection synced with store
const selectedAccountLocal = ref<string | null>(store.selectedAccount)

watch(() => store.selectedAccount, (val) => {
  selectedAccountLocal.value = val
})

function onAccountChange() {
  if (selectedAccountLocal.value) {
    store.setSelectedAccount(selectedAccountLocal.value)
  }
}

// Parse portfolio positions from MCP response
const portfolioPositions = computed(() => {
  const data = store.portfolioData
  if (!data) return []
  const items = data.items || data.positions || data.portfolio
  if (!Array.isArray(items)) return []
  return items.map((item: any) => ({
    symbol: item.code || item.name || item.symbol,
    quantity: parseFloat(item.amount || item.quantity || 0),
    price: parseFloat(item.lastPrice || item.price || 0),
    cost: parseFloat(item.cost || 0),
    value: parseFloat(item.volume || item.value || 0),
    pnl: parseFloat(item.totalGainLoss || item.pnl || 0),
    gainLossPerUnit: parseFloat(item.gainLoss || 0),
    amountToSell: parseFloat(item.amountToSell || 0)
  }))
})

const totalValue = computed(() =>
  portfolioPositions.value.reduce((sum: number, p: any) => sum + (p.value || 0), 0)
)

const totalPnl = computed(() =>
  portfolioPositions.value.reduce((sum: number, p: any) => sum + (p.pnl || 0), 0)
)

const totalPnlPercent = computed(() => {
  if (totalValue.value === 0) return '0.00'
  const costBasis = totalValue.value - totalPnl.value
  if (costBasis === 0) return '0.00'
  return ((totalPnl.value / costBasis) * 100).toFixed(2)
})

// Distribution data for the bar chart
const distributionData = computed(() => {
  if (totalValue.value === 0) return []
  return portfolioPositions.value
    .filter(p => p.value > 0)
    .map(p => ({
      symbol: p.symbol,
      value: p.value,
      percent: (p.value / totalValue.value) * 100
    }))
    .sort((a, b) => b.percent - a.percent)
})

function getDistributionColor(index: number): string {
  const colors = ['emerald', 'blue', 'indigo', 'amber', 'purple']
  return colors[index % colors.length]
}

// Parse orders from MCP response
const ordersList = computed(() => {
  const data = store.ordersData
  if (!data) return []
  const orders = data.orders || data.items
  if (!Array.isArray(orders)) return []
  return orders
    .filter((o: any) => o.stock || o.symbol || o.refNo)
    .map((o: any) => ({
      symbol: o.stock || o.symbol || '-',
      side: o.buySell === 'B' || o.buySell === 'BUY' ? 'BUY' : 'SELL',
      price: parseFloat(o.price || 0),
      quantity: parseFloat(o.size || o.quantity || 0),
      filled: parseFloat(o.executedSize || o.filled || 0),
      remaining: parseFloat(o.remainingSize || o.remaining || 0),
      status: o.status || o.statusCode || '-',
      time: o.orderTime || ''
    }))
})

// Account summary from balance items
const accountSummary = computed(() => {
  const data = store.portfolioData
  if (!data) return null
  const balanceItems = data.balanceItems
  if (Array.isArray(balanceItems) && balanceItems.length > 0) {
    const result: Record<string, string> = {}
    for (const item of balanceItems) {
      if (item.subName && item.value != null) {
        result[item.subName] = item.value
      }
    }
    return Object.keys(result).length > 0 ? result : null
  }
  if (data.summary) return data.summary
  if (data.account) return data.account
  return null
})

const lastUpdateTime = computed(() => {
  return new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
})

function formatCurrency(val: any): string {
  if (val == null || isNaN(val)) return '₺0,00'
  return '₺' + Number(val).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatNum(val: any): string {
  if (val == null || isNaN(val)) return '-'
  return Number(val).toLocaleString('tr-TR')
}

function formatKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^\w/, c => c.toUpperCase())
    .trim()
}

function formatSummaryValue(val: any): string {
  if (val == null) return '-'
  if (typeof val === 'number') return formatCurrency(val)
  if (typeof val === 'string' && /[\d.,]+/.test(val)) return '₺' + val
  return String(val)
}

function orderStatusLabel(status: string): string {
  const map: Record<string, string> = {
    'PENDING': 'Bekleyen',
    'FILLED': 'Gerçekleşti',
    'PARTIALLY_FILLED': 'Kısmi',
    'CANCELLED': 'İptal',
    'REJECTED': 'Reddedildi',
    'NEW': 'Yeni',
    'OPEN': 'Açık'
  }
  return map[status?.toUpperCase()] || status || '-'
}

function getOrderStatusClass(status: string): string {
  const s = status?.toUpperCase()
  if (s === 'FILLED') return 'filled'
  if (s === 'PENDING' || s === 'NEW' || s === 'OPEN') return 'pending'
  if (s === 'PARTIALLY_FILLED') return 'partial'
  if (s === 'CANCELLED' || s === 'REJECTED') return 'cancelled'
  return 'default'
}

function handleDisconnect() {
  store.closePortfolio()
  store.disconnect()
}
</script>

<style scoped>
.portfolio-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
}

.portfolio-panel {
  width: 100%;
  max-width: 900px;
  max-height: 85vh;
  margin: 16px;
  background: linear-gradient(180deg, #0f172a 0%, #1e293b 100%);
  border: 1px solid rgba(148, 163, 184, 0.1);
  border-radius: 16px;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header */
.portfolio-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
}

.portfolio-tabs-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(148, 163, 184, 0.1);
  border-radius: 12px;
}

.portfolio-tab {
  padding: 10px 20px;
  font-size: 13px;
  font-weight: 500;
  color: #94a3b8;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.portfolio-tab:hover {
  color: #e2e8f0;
}

.portfolio-tab.active {
  color: #fff;
  background: #6366f1;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  font-weight: 600;
}

.portfolio-header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Unified header controls */
.header-control {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  height: 40px;
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(148, 163, 184, 0.15);
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  color: #cbd5e1;
  cursor: pointer;
  transition: all 0.2s ease;
}

.header-control:hover {
  background: rgba(30, 41, 59, 0.9);
  border-color: rgba(148, 163, 184, 0.25);
}

.control-icon {
  color: #818cf8;
  flex-shrink: 0;
}

.control-label {
  white-space: nowrap;
}

/* Account Selector */
.account-selector {
  position: relative;
  padding-right: 28px;
}

.account-select {
  appearance: none;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.account-select:focus {
  outline: none;
}

.account-select option {
  background: #1e293b;
  color: #fff;
}

.select-chevron {
  position: absolute;
  right: 10px;
  color: #64748b;
  pointer-events: none;
}

/* Disconnect Button */
.disconnect-btn {
  color: #94a3b8;
}

.disconnect-btn:hover {
  color: #f43f5e;
  border-color: rgba(244, 63, 94, 0.3);
  background: rgba(244, 63, 94, 0.1);
}

.disconnect-btn .control-icon,
.disconnect-btn svg:first-child {
  color: currentColor;
}

/* Close Button */
.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(148, 163, 184, 0.15);
  border-radius: 10px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: rgba(244, 63, 94, 0.1);
  border-color: rgba(244, 63, 94, 0.3);
  color: #f43f5e;
}

.close-btn:active {
  transform: scale(0.95);
}

/* Content */
.portfolio-content {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  min-height: 300px;
}

.tab-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* KPI Cards */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}

.kpi-card {
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(148, 163, 184, 0.1);
  border-radius: 14px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.kpi-label {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.kpi-value-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.kpi-value {
  font-size: 26px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.02em;
}

.kpi-value.profit { color: #10b981; }
.kpi-value.loss { color: #f43f5e; }

.kpi-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 20px;
}

.kpi-badge.neutral {
  background: rgba(99, 102, 241, 0.1);
  color: #818cf8;
  border: 1px solid rgba(99, 102, 241, 0.2);
}

.kpi-badge.profit {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
  border: 1px solid rgba(16, 185, 129, 0.2);
}

.kpi-badge.loss {
  background: rgba(244, 63, 94, 0.1);
  color: #f43f5e;
  border: 1px solid rgba(244, 63, 94, 0.2);
}

/* Distribution Bar */
.distribution-section {
  background: rgba(15, 23, 42, 0.4);
  border: 1px solid rgba(148, 163, 184, 0.08);
  border-radius: 12px;
  padding: 16px;
}

.distribution-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-size: 12px;
  color: #94a3b8;
  font-weight: 500;
}

.distribution-legend {
  color: #64748b;
  font-size: 11px;
}

.distribution-bar {
  display: flex;
  height: 8px;
  background: #1e293b;
  border-radius: 4px;
  overflow: hidden;
}

.distribution-segment {
  height: 100%;
  transition: width 0.3s ease;
}

.distribution-segment.emerald { background: #10b981; }
.distribution-segment.blue { background: #3b82f6; }
.distribution-segment.indigo { background: #6366f1; }
.distribution-segment.amber { background: #f59e0b; }
.distribution-segment.purple { background: #a855f7; }

/* Table */
.table-wrapper {
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.08);
}

.portfolio-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.portfolio-table th {
  padding: 14px 16px;
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
  background: rgba(15, 23, 42, 0.5);
}

.portfolio-table td {
  padding: 14px 16px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.06);
}

.table-row {
  transition: background 0.15s ease;
}

.table-row:hover {
  background: rgba(30, 41, 59, 0.4);
}

.table-row:last-child td {
  border-bottom: none;
}

.symbol-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pnl-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.pnl-dot.profit { background: #10b981; }
.pnl-dot.loss { background: #f43f5e; }
.pnl-dot.neutral { background: #64748b; }

.symbol-name {
  font-weight: 700;
  color: #fff;
}

.text-left { text-align: left; }
.text-right { text-align: right; }
.text-center { text-align: center; }

.text-primary { color: #fff; }
.text-secondary { color: #cbd5e1; }
.text-muted { color: #94a3b8; }
.text-profit { color: #10b981; }
.text-loss { color: #f43f5e; }
.font-semibold { font-weight: 600; }

/* Order badges */
.order-side-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
  border-radius: 6px;
}

.order-side-badge.buy {
  background: rgba(16, 185, 129, 0.15);
  color: #10b981;
}

.order-side-badge.sell {
  background: rgba(244, 63, 94, 0.15);
  color: #f43f5e;
}

.order-status-badge {
  display: inline-flex;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 6px;
}

.order-status-badge.filled {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.order-status-badge.pending {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.order-status-badge.partial {
  background: rgba(99, 102, 241, 0.1);
  color: #818cf8;
}

.order-status-badge.cancelled {
  background: rgba(244, 63, 94, 0.1);
  color: #f43f5e;
}

.order-status-badge.default {
  background: rgba(148, 163, 184, 0.1);
  color: #94a3b8;
}

/* Summary Grid */
.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  background: rgba(15, 23, 42, 0.5);
  border: 1px solid rgba(148, 163, 184, 0.08);
  border-radius: 10px;
}

.summary-label {
  font-size: 13px;
  color: #94a3b8;
  font-weight: 500;
}

.summary-value {
  font-size: 14px;
  color: #fff;
  font-weight: 600;
}

/* Empty & Loading States */
.empty-state,
.portfolio-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 60px 20px;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
}

.empty-icon {
  color: #475569;
}

/* Footer */
.portfolio-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-top: 1px solid rgba(148, 163, 184, 0.1);
  background: rgba(15, 23, 42, 0.5);
}

.footer-hint {
  font-size: 12px;
  color: #64748b;
}

.refresh-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  color: #cbd5e1;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.refresh-btn:hover:not(:disabled) {
  background: rgba(99, 102, 241, 0.2);
  color: #fff;
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Animations */
.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.panel-fade-enter-active,
.panel-fade-leave-active {
  transition: opacity 0.25s ease;
}

.panel-fade-enter-active .portfolio-panel,
.panel-fade-leave-active .portfolio-panel {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.panel-fade-enter-from,
.panel-fade-leave-to {
  opacity: 0;
}

.panel-fade-enter-from .portfolio-panel,
.panel-fade-leave-to .portfolio-panel {
  transform: scale(0.95) translateY(10px);
  opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .portfolio-panel {
    max-width: 100%;
    max-height: 95vh;
    margin: 8px;
    border-radius: 12px;
  }

  .portfolio-header {
    padding: 16px;
  }

  .portfolio-tabs-wrapper {
    width: 100%;
    justify-content: center;
  }

  .portfolio-tab {
    padding: 8px 14px;
    font-size: 12px;
  }

  .portfolio-content {
    padding: 16px;
  }

  .kpi-value {
    font-size: 22px;
  }

  .summary-grid {
    grid-template-columns: 1fr;
  }
}
</style>
